import Series from "./series";
import MathHelper from "./math-helper";
import DefaultColors from "./default-colors";
import Color from "./color";
import StyleManager from "./style-manager"; // elements

import DoughnutLegend from "./doughnut-legend";
/**
 * @typedef DoughnutEvents
 * @type {object}
 * @property {(series: Series, ev: Event) => {}} onMouseEnter
 * @property {(ev: Event) => {}} onMouseLeave
 * @property {(ev: Event) => {}} onMouseOut
 * @property {(ev: Event)} onCentralValueClick
 * @property {(series: Series, ev: Event) => {}} onSeriesClick
 */

/**
 * @typedef DoughnutComputed
 * @type {object}
 * @property {(series: Series[], current: Series) => {}} centralValue
 * @property {(series: Series[], current: Series) => {}} topLabel
 * @property {(series: Series[], current: Series) => {}} middleLabel
 * @property {(series: Series[], current: Series) => {}} bottomLabel
 */

/**
 * @typedef ChartViewOptions
 * @type {object}
 * @property {string[]} propagateCssProperties
 * @property {string} style
 */

/**
 * @typedef DoughnutViewOptions
 * @type {object}
 * @property {ChartViewOptions} chart
 * @property {string} legend
 * @property {"number"|"percent"|"lower-percent"} suffix
 */

/**
 * @typedef DoughnutOptions
 * @type {object}
 * @property {string} el
 * @property {HTMLElement} rootElement
 * @property {Series[]} series
 * @property {DoughnutEvents} events
 * @property {DoughnutComputed} computed
 * @property {DoughnutViewOptions} view
 * @property {number} total
 */

export default class Doughnut {
  /**
   * @param {DoughnutOptions} options
   */
  constructor(options) {
    if (!options) {
      throw "parameter options is required";
    }

    if (!options.el) {
      throw "parameter options.el is required";
    } // build options


    options.rootElement = document.querySelector(options.el);
    this.options = options;
    this.validateOptions();
    StyleManager.addStyles();
    this.initialize();
  }

  validateOptions() {
    if (this.options.series == null) {
      throw "options.series is required";
    }

    this.options.pathElements = {};
    this.options.series.forEach((series, index) => {
      if (!series) {
        throw "null series at: " + index.toString();
      }

      if (typeof series.value != "number" || series.value == null || series.value < 0) {
        throw "series \"" + series.identifier + "\" value is not a valid number";
      }
    });
  }
  /**
   * @param {Node} node
   */


  clearNode(node) {
    while (node.hasChildNodes()) {
      node.removeChild(node.childNodes[0]);
    }
  }

  getTotalValue() {
    if (typeof this.options.total == "number") {
      return this.options.total;
    }

    return this.getSeriesTotalValue();
  }

  getSeriesTotalValue() {
    return this.options.series.reduce((prev, current) => {
      return prev + current.value;
    }, 0);
  }

  fillEmpty() {
    const total = this.getTotalValue();
    const seriesTotal = this.getSeriesTotalValue();
    let fill = 0;

    if (seriesTotal == 0 || this.options.series.length == 0) {
      fill = 360;
    } else if (total > 0 && total > seriesTotal) {
      fill = 360 - seriesTotal / total * 360;
    }

    if (fill > 0) {
      const startPoint = 360 - fill;
      const circle = this.createPathForSeries(total, {
        name: "Vazio",
        color: "#e0e0e0",
        empty: true,
        value: total - seriesTotal
      }, startPoint, 360 / total);
      circle.classList.add("empty-series");
      this.options.rootElement.appendChild(circle);
    }
  }
  /**
   * @param {HTMLElement} node 
   * @param {Series} series 
   */


  setSeriesNodeProperties(node, series) {
    node.classList.add("series");

    if (series.identifier) {
      node.setAttribute("data-series", series.identifier);
    }

    node.setAttribute("fill", series.color);
    node.setAttribute("stroke", series.color);
    node.setAttribute("stroke-width", "1");
    node.addEventListener("mouseenter", this.onMouseEnterEvent.bind(this, series), true);
    node.addEventListener("click", this.onSeriesClickEvent.bind(this, series), true);
  }

  removeSufix() {
    const existing = this.options.rootElement.querySelector(".percent-lower-suffix");

    if (existing) {
      this.options.rootElement.removeChild(existing);
    }
  }

  measureTextWidthCanvas(text, font) {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    context.font = font;
    const metrics = context.measureText(text);
    return metrics.width;
  }

  addSufix() {
    let suffix = null;

    if (this.options.view && this.options.view.suffix) {
      suffix = this.options.view.suffix;
    }

    switch (suffix) {
      case "percent":
        this.valueElement.textContent += "%";
        break;

      case "lower-percent":
        /**
         * @var {HTMLElement} percentElement
         */
        let percentElement = this.valueElement.cloneNode(true);
        percentElement.classList.add("percent-lower-suffix");
        percentElement.textContent = "%";
        this.options.rootElement.appendChild(percentElement); // update size

        const computedStyle = window.getComputedStyle(percentElement);
        let size = parseFloat(computedStyle.getPropertyValue("font-size").replace("px", ""));
        percentElement.style.fontSize = (size * 0.4).toFixed(0) + "px"; // update position

        const box = this.valueElement.getBBox();
        let offset = box.x + box.width + size * 0.2;

        if (offset < 200) {
          const valueElementComputeStyle = window.getComputedStyle(this.valueElement);
          const textWidthFromCanvas = this.measureTextWidthCanvas("\s".concat(this.valueElement.textContent), valueElementComputeStyle.getPropertyValue("font"));
          offset = 200 + textWidthFromCanvas / 2;
        }

        percentElement.setAttribute("x", offset);
        break;

      default:
      case "number":
        break;
    }
  }
  /**
   * @param {Series} series 
   */


  updateDefaultCentralValue(series) {
    if (series == null || series.empty) {
      this.valueElement.textContent = "100";
      return;
    }

    const total = this.getTotalValue();
    this.valueElement.textContent = Math.round(series.value * 100 / total);
  }

  getChartCenter() {
    return {
      x: 200,
      y: 200
    };
  }

  getRadius() {
    return 135;
  }

  getGridPoints(series, angularPosition, angularRate, radius) {
    const center = this.getChartCenter();
    const angle = {
      start: Math.ceil(angularPosition),
      end: Math.ceil(angularPosition + angularRate * series.value)
    };
    return {
      start: {
        inner: MathHelper.fixedPolarToCartesian(center.x, center.y, radius.inner, angle.start),
        outter: MathHelper.fixedPolarToCartesian(center.x, center.y, radius.outter, angle.start)
      },
      end: {
        inner: MathHelper.fixedPolarToCartesian(center.x, center.y, radius.inner, angle.end),
        outter: MathHelper.fixedPolarToCartesian(center.x, center.y, radius.outter, angle.end)
      }
    };
  }

  buildArcPath(series, angularPosition, angularRate, width) {
    const angle = {
      start: Math.ceil(angularPosition),
      end: Math.ceil(angularPosition + angularRate * series.value)
    };
    const radius = {
      inner: this.getRadius(),
      outter: this.getRadius() + width
    };
    const points = this.getGridPoints(series, angularPosition, angularRate, radius);
    var flag = angle.end - angle.start <= 180 ? 0 : 1;
    return ["M", points.start.inner.x, points.start.inner.y, "A", radius.inner, radius.inner, 0, flag, 1, points.end.inner.x, points.end.inner.y, "L", points.end.outter.x, points.end.outter.y, "A", radius.outter, radius.outter, 0, flag, 0, points.start.outter.x, points.start.outter.y, "L", points.start.inner.x, points.start.inner.y].join(" ");
  }
  /**
   * @param {Series} series
   */


  createPathForSeries(total, series, angularPosition, angularRate) {
    if (series.value >= total) {
      return this.createCircleForSeries(series);
    }

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    this.setSeriesNodeProperties(path, series);
    const pathDescription = this.buildArcPath(series, angularPosition, angularRate, 30);
    path.setAttribute("d", pathDescription);
    return path;
  }

  createAnimatedPathForSeries(total, series, angularPosition, angularRate) {
    const path = this.createPathForSeries(total, series, angularPosition, angularRate);
    this.createAnimationsForSeries(path, series, angularPosition, angularRate);
    return path;
  }

  createCircleForSeries(series) {
    const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
    this.setSeriesNodeProperties(circle, series);
    circle.setAttribute("cx", "200");
    circle.setAttribute("cy", "200");
    circle.setAttribute("r", (this.getRadius() + 30).toString());
    this.createAnimationsForSeries(circle, series);
    return circle;
  }

  fillInnerCircle() {
    const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
    circle.setAttribute("cx", "200");
    circle.setAttribute("cy", "200");
    circle.setAttribute("r", this.getRadius());
    circle.setAttribute("fill", "#fff");
    this.options.rootElement.appendChild(circle);
  }

  createAnimationsForSeries(node, series, angularPosition, angularRate) {
    const innerPath = this.buildArcPath(series, angularPosition, angularRate, 30);
    const outterPath = this.buildArcPath(series, angularPosition, angularRate, 40);
    const scaleInAnimation = document.createElementNS("http://www.w3.org/2000/svg", "animate");
    scaleInAnimation.setAttribute("from", innerPath);
    scaleInAnimation.setAttribute("to", outterPath);
    scaleInAnimation.setAttribute("attributeName", "d");
    scaleInAnimation.setAttribute("begin", "mouseover");
    scaleInAnimation.setAttribute("dur", "0.2s");
    scaleInAnimation.setAttribute("fill", "freeze");
    const scaleOutAnimation = document.createElementNS("http://www.w3.org/2000/svg", "animate");
    scaleOutAnimation.setAttribute("from", outterPath);
    scaleOutAnimation.setAttribute("to", innerPath);
    scaleOutAnimation.setAttribute("attributeName", "d");
    scaleOutAnimation.setAttribute("begin", "mouseout");
    scaleOutAnimation.setAttribute("dur", "0.2s");
    scaleOutAnimation.setAttribute("fill", "freeze");
    const colorInAnimation = document.createElementNS("http://www.w3.org/2000/svg", "animate");
    colorInAnimation.setAttribute("from", series.color);
    colorInAnimation.setAttribute("to", Color.lighten(series.color, 0.3));
    colorInAnimation.setAttribute("attributeName", "fill");
    colorInAnimation.setAttribute("begin", "mouseover");
    colorInAnimation.setAttribute("dur", "0.2s");
    colorInAnimation.setAttribute("fill", "freeze");
    const colorOutAnimation = document.createElementNS("http://www.w3.org/2000/svg", "animate");
    colorOutAnimation.setAttribute("from", Color.lighten(series.color, 0.3));
    colorOutAnimation.setAttribute("to", series.color);
    colorOutAnimation.setAttribute("attributeName", "fill");
    colorOutAnimation.setAttribute("begin", "mouseout");
    colorOutAnimation.setAttribute("dur", "0.2s");
    colorOutAnimation.setAttribute("fill", "freeze");
    const strokenColorInAnimation = document.createElementNS("http://www.w3.org/2000/svg", "animate");
    strokenColorInAnimation.setAttribute("from", series.color);
    strokenColorInAnimation.setAttribute("to", Color.lighten(series.color, 0.3));
    strokenColorInAnimation.setAttribute("attributeName", "stroke");
    strokenColorInAnimation.setAttribute("begin", "mouseover");
    strokenColorInAnimation.setAttribute("dur", "0.2s");
    strokenColorInAnimation.setAttribute("fill", "freeze");
    const strokenColorOutAnimation = document.createElementNS("http://www.w3.org/2000/svg", "animate");
    strokenColorOutAnimation.setAttribute("from", Color.lighten(series.color, 0.3));
    strokenColorOutAnimation.setAttribute("to", series.color);
    strokenColorOutAnimation.setAttribute("attributeName", "stroke");
    strokenColorOutAnimation.setAttribute("begin", "mouseout");
    strokenColorOutAnimation.setAttribute("dur", "0.2s");
    strokenColorOutAnimation.setAttribute("fill", "freeze");
    node.appendChild(scaleInAnimation);
    node.appendChild(scaleOutAnimation);
    node.appendChild(colorInAnimation);
    node.appendChild(colorOutAnimation);
    node.appendChild(strokenColorInAnimation);
    node.appendChild(strokenColorOutAnimation);
  }

  initialize() {
    this.options.view = this.options.view || {};
    this.options.rootElement.addEventListener("mouseleave", this.onMouseLeaveEvent.bind(this), true);
    this.options.rootElement.addEventListener("mouseout", this.onMouseOutEvent.bind(this), true);
  }
  /**
   * @param {HTMLElement} element
   */


  propagateProperties(element) {
    if (!this.options.view || !this.options.view.chart || !this.options.view.chart.propagateCssProperties) {
      return;
    }

    const computedStyle = window.getComputedStyle(this.options.rootElement);
    this.options.view.chart.propagateCssProperties.forEach(property => {
      element.style.setProperty(property, computedStyle.getPropertyValue(property));
    });
  }

  createLabel({
    identifier,
    x,
    y
  }) {
    const label = document.createElementNS("http://www.w3.org/2000/svg", "text");
    label.setAttribute("x", x.toString());
    label.setAttribute("y", y.toString());
    label.setAttribute("data-label", identifier);
    label.setAttribute("fill", "#888");
    label.setAttribute("font-family", "Roboto");
    label.setAttribute("font-size", "24px");
    label.setAttribute("text-anchor", "middle");
    return label;
  }

  draw() {
    this.clearNode(this.options.rootElement);
    this.options.rootElement.classList.add("doughnut-root-svg");
    this.fillEmpty();
    const total = this.getTotalValue();
    const angularRate = 360 / total;
    let angularPosition = 0;

    for (let x = 0; x < this.options.series.length; x++) {
      const series = this.options.series[x];

      if (!series.color) {
        series.color = DefaultColors[x];
      }

      if (!series.value) {
        continue;
      }

      const path = this.createAnimatedPathForSeries(total, series, angularPosition, angularRate);
      this.options.pathElements[x] = path;
      this.options.rootElement.appendChild(path);
      angularPosition += angularRate * series.value;
    }

    this.fillInnerCircle(); // central value

    this.valueElement = document.createElementNS("http://www.w3.org/2000/svg", "text");
    this.valueElement.setAttribute("x", "200");
    this.valueElement.setAttribute("y", "220");
    this.valueElement.setAttribute("fill", "#555");
    this.valueElement.setAttribute("font-family", "Roboto");
    this.valueElement.setAttribute("font-size", "48px");
    this.valueElement.setAttribute("text-anchor", "middle");
    this.valueElement.addEventListener("click", this.onCentralValueClickEvent.bind(this), true);
    this.propagateProperties(this.valueElement);
    this.options.rootElement.appendChild(this.valueElement); // label

    this.topLabel = this.createLabel({
      identifier: "top-label",
      x: 200,
      y: 150
    });
    this.middleLabel = this.createLabel({
      identifier: "mid-label",
      x: 200,
      y: 260
    });
    this.bottomLabel = this.createLabel({
      identifier: "bottom-label",
      x: 200,
      y: 300
    });
    this.options.rootElement.appendChild(this.topLabel);
    this.options.rootElement.appendChild(this.middleLabel);
    this.options.rootElement.appendChild(this.bottomLabel);
    this.propagateProperties(this.topLabel);
    this.propagateProperties(this.middleLabel);
    this.propagateProperties(this.bottomLabel);
    this.drawLegend();

    if (this.options && this.options.view && this.options.view.chart && this.options.view.chart.style && typeof this.options.view.chart.style === "string") {
      const styleElement = document.createElement("style");
      styleElement.textContent = this.options.view.chart.style;
      this.options.rootElement.insertBefore(styleElement, this.options.rootElement.firstChild);
    }

    this.currentSeries = null;
    this.updateView();
  }

  drawLegend() {
    if (!this.options.view || !this.options.view.legend) {
      return;
    }

    this.legendElement = document.querySelector(this.options.view.legend);

    while (this.legendElement.hasChildNodes()) {
      this.legendElement.removeChild(this.legendElement.childNodes[0]);
    }

    for (let x = 0; x < this.options.series.length; x++) {
      /**
       * @type {Series} series
       */
      const series = this.options.series[x];
      let style = null;
      let styleOverride = false;

      if (series.legend) {
        style = series.legend.style;
        styleOverride = series.legend.styleOverride;
      }

      const elem = new DoughnutLegend(series.name, series.color, {
        style,
        styleOverride
      });

      if (series.identifier) {
        elem.setAttribute("data-series", series.identifier);
      }

      elem.addEventListener("mouseenter", this.onMouseEnterLegend.bind(this, series, x), true);
      elem.addEventListener("mouseout", this.onMouseOutLegend.bind(this, series, x), true);
      this.legendElement.appendChild(elem);
    }
  }

  onMouseEnterLegend(series, index, ev) {
    let target = this.options.pathElements[index];
    if (!target) throw 'cant get path element';
    this.currentSeries = series;
    this.options.rootElement.classList.add("active");
    target.classList.add("active");
    this.updateView();
  }

  onMouseOutLegend(series, index, ev) {
    let target = this.options.pathElements[index];
    if (!target) throw 'cant get path element';
    this.currentSeries = null;
    this.options.rootElement.classList.remove("active");
    target.classList.remove("active");
    this.updateView();
  }

  getBottomLabel() {
    return this.bottomLabel;
  }

  getMiddleLabel() {
    return this.middleLabel;
  }

  getTopLabel() {
    return this.topLabel;
  }

  updateLabel() {
    const labels = {
      bottomLabel: this.bottomLabel,
      middleLabel: this.middleLabel,
      topLabel: this.topLabel
    };

    for (const key in labels) {
      const target = labels[key];
      let labelValue = null;

      if (this.options.computed && this.options.computed[key]) {
        labelValue = this.options.computed[key](this.options.series, this.currentSeries, this);
      } else {
        // default label value
        // title of series or total
        labelValue = this.getDefaultLabelValue(key);
      }

      target.textContent = labelValue;
    }
  }
  /**
   * @param {string} name 
   */


  getDefaultLabelValue(name) {
    switch (name.toLowerCase()) {
      case "toplabel":
      case "bottomlabel":
        return null;

      case "middlelabel":
        if (this.currentSeries && this.currentSeries.empty) {
          return null;
        }

        if (this.currentSeries != null) {
          return this.currentSeries.name;
        }

        return null;

      default:
        throw 'invalid label key';
    }
  }

  updateView() {
    this.removeSufix();
    this.updateCentralValue(this.currentSeries);
    this.updateLabel(this.currentSeries);
  }

  updateCentralValue(series) {
    if (this.options.computed && this.options.computed.centralValue) {
      this.valueElement.textContent = this.options.computed.centralValue(this.options.series, series, this);
    } else {
      this.updateDefaultCentralValue(series);
    }

    this.addSufix();
  }
  /** events */


  onMouseEnterEvent(series, ev) {
    if (this.options.events && this.options.events.onMouseEnter) {
      this.options.events.onMouseEnter(series, ev);
    } // central value


    this.currentSeries = series;
    this.updateView();
  }

  onMouseLeaveEvent(ev) {
    if (this.options.events && this.options.events.onMouseLeave) {
      this.options.events.onMouseLeave(ev);
    } // central value


    this.currentSeries = null;
    this.updateView();
  }

  onMouseOutEvent(ev) {
    if (this.options.events && this.options.events.onMouseOut) {
      this.options.events.onMouseOut(ev);
    }
  }

  onCentralValueClickEvent(ev) {
    if (this.options.events && this.options.events.onCentralValueClick) {
      this.options.events.onCentralValueClick(ev);
    }
  }

  onSeriesClickEvent(series, ev) {
    if (this.options.events && this.options.events.onSeriesClick) {
      this.options.events.onSeriesClick(series, ev);
    } // central value


    this.currentSeries = series;
    this.updateView();
  }

}